/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './TimelineDot';

const TimelineDotManager: ComponentManagerFn<'TimelineDot'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'TimelineDot'>(platform, 'TimelineDot'),
    type: 'TimelineDot',
    name: 'Timeline Dot',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'Icon'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      bgcolor: {
        label: 'Background Color',
        type: 'ColorExpression',
      },
      borderColor: {
        label: 'Border',
        type: 'ColorExpression',
        returnType: () => 'string',
      },
      borderWidth: {
        label: 'Border Width',
        type: 'number',
        min: 0,
        max: 8,
      },
    },
    getDisplayText: (item) => 'Timeline Dot',
    category: 'Forms',
    icon: { iconName: 'dot-circle' },
    description: `**Overview**<br/>
The Timeline Dot is the focal point of each event in a **Timeline** visualization. It renders as a circular indicator on the timeline axis, representing a specific milestone or step. It can be a simple colored dot or a container for an icon.
Use this component to mark the occurrence of an event, often color-coding it to represent status (e.g., Green for "Done", Grey for "Pending").

**1. Visual Styling**<br/>
You have granular control over the dot's appearance to convey meaning through visual cues.
* **Background Color:** Sets the fill color of the circle.
* **Border Color:** Sets the color of the ring around the dot.
* **Border Width:** Controls the thickness of the ring (0px - 8px).
* **Style Tip:** Use a transparent background with a colored border for an "outlined" look, or a solid background for a "filled" look.

**2. Icon Support**<br/>
The Timeline Dot can host an **Icon** child component to add more context to the milestone.
* **Without Icon:** It renders as a simple, minimalist bullet point.
* **With Icon:** It acts as a circular wrapper around the icon (e.g., placing a "Truck" icon inside the dot for a "Shipped" event).

**3. Interactivity**<br/>
* **TriggerOnClick:** You can make the dot clickable to trigger actions.
* **Functionality:** Clicking the dot could open a modal with detailed logs, navigate to a related record, or expand a hidden section of the timeline.

**4. Best Use Cases**<br/>
* **Milestones:** Using a "Star" icon inside a yellow dot for major project goals or achievements.
* **Status Tracking:** Using a color system: Green (Completed), Blue (In Progress), and Grey (Future/Planned).
* **Alerts:** A Red Dot with an "Exclamation" icon to highlight failed steps or errors in a process flow.`,
  };
};

export default TimelineDotManager;
